import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { NumeroOpcionalInput } from './campos';

/**
 * Discordar de um número que o sistema calculou — com o motivo junto (Fase 24).
 *
 * Serve às duas correções da fase: o medido do serviço que o desenho mede (D4) e a
 * quantidade do item que o índice calcula (D9). As duas têm a mesma forma e a mesma razão
 * de existir: o número calculado tem de onde vir, e o digitado por cima dele só se explica
 * pelo motivo — seis meses depois, "118,40" no lugar de "121,80" sem uma palavra ao lado é
 * um número que alguém acaba apagando por não entender.
 *
 * Por isso o motivo não é um campo opcional ao lado: **aplicar só existe com ele**. E nada
 * vai para a bancada a cada tecla, ao contrário do campo solto — a correção é um ato, e
 * meia correção sem motivo não chega a existir.
 */
export function CorrecaoComMotivo({
  rotulo,
  inicial,
  motivoInicial,
  unidade,
  onAplicar,
  onCancelar,
}: {
  /** O nome acessível do campo do número — "Medido corrigido de ASSENTAMENTO". */
  rotulo: string;
  inicial: number | undefined;
  motivoInicial?: string;
  /** A unidade ao lado do número, quando há uma a dizer. */
  unidade?: string;
  onAplicar: (valor: number, motivo: string) => void;
  onCancelar: () => void;
}) {
  const [valor, setValor] = useState<number | undefined>(inicial);
  const [motivo, setMotivo] = useState(motivoInicial ?? '');
  const pronto = valor !== undefined && motivo.trim() !== '';

  return (
    <form
      className="flex min-w-56 flex-col gap-1.5"
      onSubmit={(event) => {
        event.preventDefault();
        if (pronto) onAplicar(valor, motivo.trim());
      }}
    >
      <div className="flex items-center gap-1.5">
        <NumeroOpcionalInput
          aria-label={rotulo}
          className="h-9 w-28 text-right"
          value={valor}
          onChange={setValor}
          autoFocus
        />
        {unidade !== undefined && (
          <span className="text-xs text-(--color-content-muted)">{unidade}</span>
        )}
      </div>
      <Input
        aria-label={`Motivo — ${rotulo}`}
        placeholder="Por que o número é outro?"
        className="h-9"
        value={motivo}
        onChange={(event) => setMotivo(event.target.value)}
      />
      <div className="flex items-center gap-1.5">
        <Button type="submit" size="sm" variant="secondary" disabled={!pronto}>
          Aplicar
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancelar}>
          Cancelar
        </Button>
      </div>
    </form>
  );
}
